import { message } from 'antd';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import useLazyQueryWithCallbacks from '../common/CommonUseLazyQuery';
import { FILE_SIZE_10MB } from '../common/constants';
import { fileUpload } from '../common/utils';
import { GET_URL, GET_URL_BY_KEY } from '../modules/profile/graphql/Queries';

const TextEditor = ({
  setEditorValue,
  className = '',
  placeholder,
  toggleImageLoader,
  readOnly = false,
  ...rest
}) => {
  /** Store pending image URL in ref to avoid race conditions */
  const pendingImageUrlRef = useRef(null);

  /** Store image handler in ref to keep modules stable */
  const imageHandlerRef = useRef(null);

  /** Store quill editor instance */
  const quillEditorRef = useRef(null);

  const [value, setValue] = useState('');
  const [loader, setLoader] = useState(false);
  const [file, setFile] = useState(null);
  const [pendingImageUrl, setPendingImageUrl] = useState(null);

  /** Track mount state to prevent post-unmount side effects */
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /* -------------------- GET IMAGE VIEW URL -------------------- */
  const [getImagesSignedUrls] = useLazyQueryWithCallbacks(GET_URL_BY_KEY, {
    fetchPolicy: 'network-only',
    onCompleted: (responseData) => {
      const imageUrl = responseData?.getImagesSignedUrls?.[0];
      if (imageUrl) {
        pendingImageUrlRef.current = imageUrl;
        setPendingImageUrl(imageUrl);
      }
    },
    onError: () => {},
  });

  /** Helper to get Quill instance via DOM */
  const getQuillInstance = useCallback(() => {
    if (quillEditorRef?.current) {
      return quillEditorRef?.current;
    }

    // eslint-disable-next-line no-undef
    const qlContainer = document?.querySelector(
      '.editor-component .ql-container',
    );
    if (qlContainer) {
      const quillInstance = Quill?.find(qlContainer);
      if (quillInstance) {
        quillEditorRef.current = quillInstance;
        return quillInstance;
      }
    }

    return null;
  }, []);

  /* -------------------- INSERT IMAGE WHEN QUILL IS READY -------------------- */
  useEffect(() => {
    const imageUrl = pendingImageUrlRef?.current;
    if (!imageUrl) return;

    const quill = getQuillInstance();

    // If Quill not ready, retry after a brief delay
    if (!quill) {
      const retryTimeout = setTimeout(() => {
        if (!isMountedRef.current) return;

        const urlToInsert = pendingImageUrlRef?.current;
        if (!urlToInsert) return;

        const quillInstance = getQuillInstance();
        if (!quillInstance) return;
        try {
          quillInstance?.focus();
          const range = quillInstance?.getSelection(true);
          const safeIndex =
            typeof range?.index === 'number'
              ? range.index
              : (quillInstance?.getLength() ?? 0);
          quillInstance?.insertEmbed(safeIndex, 'image', urlToInsert, 'user');
          quillInstance?.setSelection(safeIndex + 1);

          if (!isMountedRef.current) return;
          const newContent = quillInstance?.root?.innerHTML;
          setValue(newContent);
          setEditorValue?.(newContent);
        } catch (error) {
          message.error('Failed to insert image into editor');
          return;
        }

        if (!isMountedRef.current) return;
        // Cleanup loader state
        pendingImageUrlRef.current = null;
        setPendingImageUrl(null);
        setLoader(false);
        toggleImageLoader?.();
      }, 300);

      return () => clearTimeout(retryTimeout);
    }

    // Quill is ready, insert image immediately
    try {
      quill?.focus();
      const range = quill?.getSelection(true);
      const safeIndex =
        typeof range?.index === 'number'
          ? range.index
          : (quill?.getLength() ?? 0);
      quill?.insertEmbed(safeIndex, 'image', imageUrl, 'user');
      quill?.setSelection(safeIndex + 1);

      if (isMountedRef.current) {
        const newContent = quill?.root?.innerHTML;
        setValue(newContent);
        setEditorValue?.(newContent);
      }
    } catch (error) {
      message.error('Failed to insert image into editor');
    } finally {
      pendingImageUrlRef.current = null;
      if (isMountedRef.current) {
        // Cleanup loader state
        setPendingImageUrl(null);
        setLoader(false);
        toggleImageLoader?.();
      }
    }

    return undefined;
  }, [pendingImageUrl, setEditorValue, toggleImageLoader, getQuillInstance]);

  /* -------------------- SIGNED PUT URL -------------------- */
  const [fetchSignedUrl] = useLazyQueryWithCallbacks(GET_URL, {
    onCompleted: (responseData) => {
      const signedUrl = responseData?.getSignedPutUrl?.signedUrl;
      const key = responseData?.getSignedPutUrl?.key;
      if (!signedUrl || !key || !file) {
        toggleImageLoader?.();
        setLoader(false);
        message.error('Image upload failed!');
        return;
      }
      fileUpload(signedUrl, file)
        ?.then(() => {
          getImagesSignedUrls({
            variables: {
              data: { keys: [key] },
            },
          });
        })
        ?.catch(() => {
          toggleImageLoader?.();
          setLoader(false);
          message.error('Image upload failed!');
        });
    },
    onError() {},
  });

  /* -------------------- CHANGE HANDLER -------------------- */
  const handleChange = useCallback(
    (newValue) => {
      setValue(newValue);
      setEditorValue?.(newValue);
    },
    [setEditorValue],
  );

  /* -------------------- IMAGE HANDLER -------------------- */
  const imageHandler = useCallback(() => {
    if (readOnly) return;

    // eslint-disable-next-line no-undef
    const input = document?.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.click();

    input.onchange = () => {
      const selectedFile = input?.files?.[0];
      if (!selectedFile) return;

      if (selectedFile?.size > FILE_SIZE_10MB) {
        return message.error('File size should not be greater than 10MB');
      }
      const { name, type: contentType } = selectedFile;
      const fileName = name?.substring(0, name?.lastIndexOf('.'));
      setFile(selectedFile);
      setLoader(true);
      toggleImageLoader?.();
      fetchSignedUrl({
        variables: {
          data: {
            fileName,
            contentType,
            acl: 'private',
          },
        },
      });
    };
  }, [readOnly, toggleImageLoader, fetchSignedUrl]);

  /* -------------------- UPDATE IMAGE HANDLER REF -------------------- */
  useEffect(() => {
    imageHandlerRef.current = imageHandler;
  }, [imageHandler]);

  /* -------------------- STABLE IMAGE HANDLER WRAPPER -------------------- */
  const stableImageHandler = useCallback(() => {
    imageHandlerRef?.current?.();
  }, []);

  /* -------------------- QUILL MODULES -------------------- */
  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          ['bold', 'italic', 'underline', 'link'],
          ['image'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['clean'],
        ],
        handlers: {
          image: stableImageHandler,
        },
      },
    }),
    [stableImageHandler],
  );

  return (
    <>
      <ReactQuill
        className={`editor-component ${className}`}
        theme="snow"
        placeholder={placeholder}
        modules={modules}
        value={value || ''}
        readOnly={readOnly}
        onChange={handleChange}
        {...rest}
      />
      {loader && (
        <div className="text-danger file-size-warning">
          <i className="fa fa-spin fa-spinner mr-2" />
          Image upload in progress, please wait.
        </div>
      )}
    </>
  );
};

export default TextEditor;
